import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { UserSeat } from 'src/app/Services/WOWApi.Service';

@Component({
  selector: 'app-user-seat',
  templateUrl: './user-seat.component.html',
  styleUrls: ['./user-seat.component.css'],
  standalone: false
})
export class UserSeatComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() seats: UserSeat[] = [];
  @Input() selectedSeat: string | number | null | undefined;
  @Input() webinarName: string | undefined;
  /**
   * Seconds the Azure Function keeps DisplaySeats on screen.
   * The tour uses a readable pace and finishes inside this window when it can;
   * it will not speed into a blur just to fit.
   */
  @Input() displaySeatsDelaySeconds: number | undefined;
  @Output() introScrollComplete = new EventEmitter<void>();
  @ViewChild('seatsGrid') seatsGrid: ElementRef<HTMLDivElement>;

  revealed: { [seatNumber: number]: boolean } = {};
  focusedSeat = 0;
  tourProgress = 0;
  tourLabel = 'Preparing the field…';
  tourActive = false;

  private static readonly INITIAL_DELAY_MS = 350;
  /**
   * Scroll/pause budget per viewport of seats. Kept deliberately brisk - the earlier
   * 1.8s scroll plus 1.2s dwell made the tour feel stalled between pages. The backend
   * DisplaySeats window (see CalculateSeatsDisplayDelay) is sized against these numbers,
   * so raising them here without raising it there ends the tour early on a dead screen.
   */
  private static readonly DEFAULT_PAGE_SCROLL_MS = 1100;
  private static readonly DEFAULT_PAGE_PAUSE_MS = 600;
  private static readonly MIN_PAGE_SCROLL_MS = 650;
  private static readonly MIN_PAGE_PAUSE_MS = 300;
  private static readonly MAX_PAGE_SCROLL_MS = 1500;
  private static readonly MAX_PAGE_PAUSE_MS = 850;
  private static readonly PAGE_OVERLAP_RATIO = 0.18;
  private static readonly END_DWELL_MS = 700;

  private pageScrollDurationMs = UserSeatComponent.DEFAULT_PAGE_SCROLL_MS;
  private pagePauseMs = UserSeatComponent.DEFAULT_PAGE_PAUSE_MS;
  private hasAutoScrolled = false;
  private introCompleteEmitted = false;
  private cancelled = false;
  private viewReady = false;
  private rafId = 0;
  private timeouts: ReturnType<typeof setTimeout>[] = [];
  private io: IntersectionObserver | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  get seatCount(): number {
    return this.seats?.length || 0;
  }

  get selectedSeatNumber(): number {
    const n = Number(this.selectedSeat);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  isRevealed(seatNumber: number): boolean {
    return !!this.revealed[seatNumber] || this.introCompleteEmitted;
  }

  isFocused(seatNumber: number): boolean {
    return this.focusedSeat === seatNumber;
  }

  isPicked(seatNumber: number): boolean {
    return this.selectedSeatNumber > 0 && seatNumber === this.selectedSeatNumber;
  }

  trackBySeat(_index: number, seat: UserSeat): number {
    return seat.seatId || seat.seatNumber;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.observeCards();
    this.queue(UserSeatComponent.INITIAL_DELAY_MS, () => this.startAutoScroll());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['seats'] && this.seats?.length && this.viewReady && !this.hasAutoScrolled) {
      this.observeCards();
      this.queue(UserSeatComponent.INITIAL_DELAY_MS, () => this.startAutoScroll());
    }
    if (changes['selectedSeat'] && this.introCompleteEmitted) {
      this.scrollSelectedIntoView();
    }
  }

  ngOnDestroy(): void {
    this.cancelled = true;
    this.clearTimers();
    this.io?.disconnect();
    this.io = null;
  }

  private startAutoScroll(): void {
    if (this.hasAutoScrolled || this.cancelled || !this.seats?.length) {
      return;
    }

    const startY = this.gridTopY();
    const endY = this.tourMaxY();
    if (endY - startY <= 24) {
      this.hasAutoScrolled = true;
      this.revealAll();
      this.tourProgress = 1;
      this.tourLabel = `${this.seatCount} seats in the draw`;
      this.emitIntroComplete();
      this.cdr.markForCheck();
      return;
    }

    this.hasAutoScrolled = true;
    this.tourActive = true;
    this.applyScrollTimings(this.estimateScrollSteps(endY - startY));
    window.scrollTo(0, startY);
    this.refreshViewportState();

    this.queue(this.pagePauseMs, () => this.scrollPagesDown(() => {
      this.tourActive = false;
      this.revealAll();
      this.tourProgress = 1;
      this.tourLabel = `All ${this.seatCount} seats are in`;
      this.cdr.markForCheck();
      this.queue(UserSeatComponent.END_DWELL_MS, () => this.emitIntroComplete());
    }));
  }

  private estimateScrollSteps(maxY: number): number {
    const step = window.innerHeight * (1 - UserSeatComponent.PAGE_OVERLAP_RATIO);
    if (step <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(maxY / step));
  }

  private applyScrollTimings(totalSteps: number): void {
    const defaultScroll = UserSeatComponent.DEFAULT_PAGE_SCROLL_MS;
    const defaultPause = UserSeatComponent.DEFAULT_PAGE_PAUSE_MS;
    this.pageScrollDurationMs = defaultScroll;
    this.pagePauseMs = defaultPause;

    if (totalSteps < 1 || !this.displaySeatsDelaySeconds || this.displaySeatsDelaySeconds <= 0) {
      return;
    }

    const budgetMs = Math.max(
      0,
      this.displaySeatsDelaySeconds * 1000
        - UserSeatComponent.INITIAL_DELAY_MS
        - UserSeatComponent.END_DWELL_MS
        - 400
    );
    const defaultCycle = totalSteps * defaultScroll + (totalSteps + 1) * defaultPause;
    if (defaultCycle <= budgetMs) {
      const extra = budgetMs - defaultCycle;
      const bonus = Math.floor(extra / Math.max(1, totalSteps * 2));
      this.pageScrollDurationMs = Math.min(UserSeatComponent.MAX_PAGE_SCROLL_MS, defaultScroll + bonus);
      this.pagePauseMs = Math.min(UserSeatComponent.MAX_PAGE_PAUSE_MS, defaultPause + bonus);
      return;
    }

    const ratio = budgetMs / defaultCycle;
    this.pageScrollDurationMs = Math.max(
      UserSeatComponent.MIN_PAGE_SCROLL_MS,
      Math.floor(defaultScroll * ratio)
    );
    this.pagePauseMs = Math.max(
      UserSeatComponent.MIN_PAGE_PAUSE_MS,
      Math.floor(defaultPause * ratio)
    );
  }

  private scrollPagesDown(onComplete: () => void): void {
    if (this.cancelled) {
      onComplete();
      return;
    }

    const currentY = this.scrollY();
    const maxY = this.tourMaxY();
    this.refreshViewportState();

    if (currentY >= maxY - 2) {
      onComplete();
      return;
    }

    const step = window.innerHeight * (1 - UserSeatComponent.PAGE_OVERLAP_RATIO);
    const nextY = Math.min(maxY, currentY + step);
    this.smoothScrollTo(nextY, () => {
      this.refreshViewportState();
      this.queue(this.pagePauseMs, () => this.scrollPagesDown(onComplete));
    });
  }

  private smoothScrollTo(targetY: number, onComplete: () => void): void {
    const startY = this.scrollY();
    const distance = targetY - startY;
    if (Math.abs(distance) < 1) {
      onComplete();
      return;
    }

    const duration = this.pageScrollDurationMs;
    const startTime = performance.now();
    const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const step = (now: number) => {
      if (this.cancelled) {
        return;
      }
      const progress = Math.min(1, (now - startTime) / duration);
      window.scrollTo(0, startY + distance * ease(progress));
      if (progress < 0.08 || progress > 0.92 || Math.round(progress * 20) % 4 === 0) {
        this.refreshViewportState();
      }
      if (progress < 1) {
        this.rafId = requestAnimationFrame(step);
      } else {
        onComplete();
      }
    };
    this.rafId = requestAnimationFrame(step);
  }

  private refreshViewportState(): void {
    const cards = this.seatsGrid?.nativeElement?.querySelectorAll('.user-seat-card');
    if (!cards || !cards.length) {
      return;
    }
    const bandTop = window.innerHeight * 0.2;
    const bandBottom = window.innerHeight * 0.78;
    let firstVisible = 0;
    let lastVisible = 0;
    let focus = 0;
    cards.forEach((node) => {
      const el = node as HTMLElement;
      const num = Number(el.dataset.seat);
      const r = el.getBoundingClientRect();
      const visible = r.bottom > 80 && r.top < window.innerHeight - 24;
      if (visible) {
        this.revealed[num] = true;
        if (!firstVisible) {
          firstVisible = num;
        }
        lastVisible = num;
        const mid = (r.top + r.bottom) / 2;
        if (!focus && mid >= bandTop && mid <= bandBottom) {
          focus = num;
        }
      }
    });
    this.focusedSeat = focus || lastVisible;
    const maxY = Math.max(1, this.tourMaxY() - this.gridTopY());
    this.tourProgress = Math.min(1, Math.max(0, (this.scrollY() - this.gridTopY()) / maxY));
    if (firstVisible && lastVisible) {
      this.tourLabel =
        firstVisible === lastVisible
          ? `Seat ${firstVisible} of ${this.seatCount}`
          : `Seats ${firstVisible}–${lastVisible} of ${this.seatCount}`;
    }
    this.cdr.markForCheck();
  }

  private observeCards(): void {
    this.io?.disconnect();
    const root = this.seatsGrid?.nativeElement;
    if (!root) {
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        let changed = false;
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }
          const num = Number((entry.target as HTMLElement).dataset.seat);
          if (num && !this.revealed[num]) {
            this.revealed[num] = true;
            changed = true;
          }
        }
        if (changed) {
          this.cdr.markForCheck();
        }
      },
      { threshold: 0.35 }
    );
    root.querySelectorAll('.user-seat-card').forEach((el) => this.io!.observe(el));
  }

  private scrollSelectedIntoView(): void {
    if (!this.selectedSeatNumber) {
      return;
    }
    this.queue(80, () => {
      const el = document.getElementById('seat' + this.selectedSeatNumber);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
  }

  private revealAll(): void {
    (this.seats || []).forEach((seat) => {
      this.revealed[seat.seatNumber] = true;
    });
  }

  private emitIntroComplete(): void {
    if (this.introCompleteEmitted) {
      return;
    }
    this.introCompleteEmitted = true;
    this.introScrollComplete.emit();
  }

  private gridTopY(): number {
    const grid = this.seatsGrid?.nativeElement;
    if (!grid) {
      return 0;
    }
    const top = grid.getBoundingClientRect().top + this.scrollY();
    return Math.max(0, top - this.stickyOffset());
  }

  private tourMaxY(): number {
    const grid = this.seatsGrid?.nativeElement;
    const last = grid?.querySelector('.user-seat-card:last-child') as HTMLElement | null;
    if (!last) {
      return this.maxScrollY();
    }
    const bottom = last.getBoundingClientRect().bottom + this.scrollY();
    return Math.max(0, bottom - window.innerHeight + 20);
  }

  private stickyOffset(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-header-offset');
    const header = parseFloat(raw) || 72;
    return header + 56;
  }

  private maxScrollY(): number {
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    return Math.max(0, height - window.innerHeight);
  }

  private scrollY(): number {
    return window.scrollY || window.pageYOffset || 0;
  }

  private queue(ms: number, fn: () => void): void {
    const handle = setTimeout(() => {
      this.timeouts = this.timeouts.filter((h) => h !== handle);
      if (!this.cancelled) {
        fn();
      }
    }, ms);
    this.timeouts.push(handle);
  }

  private clearTimers(): void {
    this.timeouts.forEach((h) => clearTimeout(h));
    this.timeouts = [];
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }
}
